import { useEffect, useMemo, useState } from 'react'
import { Menu, Tag } from 'antd'
import type { MenuProps } from 'antd'
import { MutedOutlined, PayCircleOutlined, ReadOutlined, AccountBookOutlined, BookOutlined, BarChartOutlined, KeyOutlined, LineChartOutlined } from '@ant-design/icons'
import { useNavigate, useLocation, useMatches } from 'react-router-dom'
import type { RouteHandle } from '@/routes/interface'
import { buildMenuItems } from '@/routes/routeConfig'
import type { MenuNode } from '@/routes/routeConfig'
import { useAuth } from '@/context/AuthContext'

const ICON_MAP: Record<string, React.ReactNode> = {
    MutedOutlined: <MutedOutlined />,
    AccountBookOutlined: <AccountBookOutlined />,
    ReadOutlined: <ReadOutlined />,
    PayCircleOutlined: <PayCircleOutlined />,
    BookOutlined: <BookOutlined />,
    BarChartOutlined: <BarChartOutlined />,
    KeyOutlined: <KeyOutlined />,
    LineChartOutlined: <LineChartOutlined />,
}

const allMenuItems = buildMenuItems(ICON_MAP)

function getSelectedKey(pathname: string, currentRouteMeta?: RouteHandle) {
    return currentRouteMeta?.key ?? pathname
}

function nodeContainsTarget(node: MenuNode, targetKey: string): boolean {
    if (node.key === targetKey) return true
    return node.children?.some(child => nodeContainsTarget(child, targetKey)) ?? false
}

function getNestedOpenKeyByTarget(items: MenuNode[], targetKey: string): string | null {
    for (const rootItem of items) {
        for (const child of rootItem.children ?? []) {
            if (child.children?.length && nodeContainsTarget(child, targetKey)) {
                return String(child.key)
            }
        }
    }
    return null
}

function findNodeByKey(items: MenuNode[], targetKey: string): MenuNode | null {
    for (const item of items) {
        if (item.key === targetKey) return item
        if (item.children?.length) {
            const result = findNodeByKey(item.children, targetKey)
            if (result) return result
        }
    }
    return null
}

function findFirstLeafKey(node: MenuNode): string | null {
    if (!node.children?.length) return node.key.startsWith('/') ? node.key : null
    for (const child of node.children) {
        const result = findFirstLeafKey(child)
        if (result) return result
    }
    return null
}

function getNestedOpenKeys(items: MenuNode[], targetKey: string) {
    const nestedKey = getNestedOpenKeyByTarget(items, targetKey)
    return nestedKey ? [nestedKey] : []
}

function renderMenuLabel(node: MenuNode) {
    if (!node.tag) return node.label
    return (
        <span className="flex items-center gap-1">
            <Tag style={{ borderRadius: '15px' }} className='menu-tag'>{node.tag}</Tag>
            <span>{node.label}</span>
        </span>
    )
}

function withRenderedLabels(items: MenuNode[]): MenuProps['items'] {
    return items.map(item => ({
        ...item,
        label: renderMenuLabel(item),
        children: item.children ? withRenderedLabels(item.children) : undefined,
    }))
}

export default function MenuCenter() {
    const navigate = useNavigate()
    const matches = useMatches()
    const currentMatch = matches[matches.length - 1]
    const currentRouteMeta = currentMatch?.handle as RouteHandle | undefined
    const { pathname } = useLocation()
    const { status } = useAuth()
    // 账户菜单仅登录后展示
    const isAuthenticated = status === 'authenticated'
    const menuItems = useMemo(
        () => isAuthenticated ? allMenuItems : allMenuItems.filter(item => item.key !== 'Account'),
        [isAuthenticated],
    )
    const rootOpenKeys = useMemo(() => menuItems.map(item => String(item.key)), [menuItems])
    const selectedKey = getSelectedKey(pathname, currentRouteMeta)
    const [nestedOpenKeys, setNestedOpenKeys] = useState<string[]>(() => getNestedOpenKeys(menuItems, selectedKey))
    const openKeys = Array.from(new Set([...rootOpenKeys, ...nestedOpenKeys]))

    useEffect(() => {
        setNestedOpenKeys(prevKeys => {
            const currentKeys = getNestedOpenKeys(menuItems, selectedKey)
            return Array.from(new Set([...prevKeys, ...currentKeys]))
        })
    }, [selectedKey, menuItems])

    const handleClick: MenuProps['onClick'] = ({ key }) => {
        navigate(String(key))
    }

    const handleOpenChange: MenuProps['onOpenChange'] = keys => {
        const nextNestedOpenKeys = keys.map(String).filter(key => !rootOpenKeys.includes(key))
        const openedKey = nextNestedOpenKeys.find(key => !nestedOpenKeys.includes(key))

        if (openedKey) {
            const openedNode = findNodeByKey(menuItems, openedKey)
            const firstLeafKey = openedNode && findFirstLeafKey(openedNode)
            if (firstLeafKey) navigate(firstLeafKey)
        }

        setNestedOpenKeys(nextNestedOpenKeys)
    }

    return (
        <Menu
            mode="inline"
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            selectedKeys={selectedKey ? [selectedKey] : []}
            items={withRenderedLabels(menuItems)}
            onClick={handleClick}
            style={{ borderRight: 0, marginTop: 4, background: 'rgb(var(--background))' }}
        />
    )
}